import { supabase } from '../lib/supabase';

/**
 * Settings Service
 * Handles all settings operations including:
 * - Company profile (logo, address, contact, business hours)
 * - Company settings (invoice, notifications, branding)
 * - Job templates
 * - Notification preferences (per-user)
 * - User profile (avatar, display preferences)
 * - Data export
 */
export const settingsService = {
  // ==========================================
  // COMPANY PROFILE
  // ==========================================

  /**
   * Get company profile with all settings
   * @param {string} companyId - Company UUID
   */
  async getCompanyProfile(companyId) {
    // First try to get all extended columns
    let { data, error } = await supabase
      .from('companies')
      .select(`
        id,
        company_name,
        company_tag,
        logo_url,
        address_line1,
        address_line2,
        city,
        state,
        postcode,
        country,
        phone,
        contact_email,
        website,
        timezone,
        business_hours,
        created_at,
        updated_at
      `)
      .eq('id', companyId)
      .single();

    // If extended columns don't exist, fall back to basic columns
    if (error?.message?.includes('column') || error?.code === '42703') {
      const basicResult = await supabase
        .from('companies')
        .select('id, company_name, company_tag, created_at, updated_at')
        .eq('id', companyId)
        .single();

      if (basicResult.error) throw new Error(basicResult.error.message);
      return { success: true, data: basicResult.data };
    }

    if (error) throw new Error(error.message);
    return { success: true, data };
  },

  /**
   * Update company profile
   * @param {string} companyId - Company UUID
   * @param {object} updates - Fields to update
   */
  async updateCompanyProfile(companyId, updates) {
    const { data, error } = await supabase
      .from('companies')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', companyId)
      .select()
      .single();

    // If columns don't exist, show a friendly message
    if (error?.message?.includes('column') || error?.code === '42703') {
      throw new Error('Company profile columns not configured. Please run database migration.');
    }

    if (error) throw new Error(error.message);
    return { success: true, data };
  },

  /**
   * Upload company logo
   * @param {string} companyId - Company UUID
   * @param {File} file - Image file to upload
   */
  async uploadLogo(companyId, file) {
    const fileExt = file.name.split('.').pop().toLowerCase();
    const fileName = `${companyId}/logo.${fileExt}`;

    // Try to delete existing logo files first (ignore errors)
    try {
      await supabase.storage
        .from('company-logos')
        .remove([`${companyId}/logo.png`, `${companyId}/logo.jpg`, `${companyId}/logo.jpeg`, `${companyId}/logo.webp`]);
    } catch (e) {
      console.warn('Could not delete existing logos:', e.message);
    }

    // Upload new logo
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('company-logos')
      .upload(fileName, file, {
        upsert: true,
        cacheControl: '3600'
      });

    if (uploadError) {
      console.error('Logo upload error:', uploadError);
      throw new Error(uploadError.message || 'Failed to upload logo');
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('company-logos')
      .getPublicUrl(fileName);

    // Update company record with new logo URL (add cache buster)
    const logoUrl = `${urlData.publicUrl}?t=${Date.now()}`;
    await this.updateCompanyProfile(companyId, { logo_url: logoUrl });

    return { success: true, url: logoUrl };
  },

  /**
   * Delete company logo
   * @param {string} companyId - Company UUID
   */
  async deleteLogo(companyId) {
    // Remove all possible logo files
    await supabase.storage
      .from('company-logos')
      .remove([`${companyId}/logo.png`, `${companyId}/logo.jpg`, `${companyId}/logo.jpeg`, `${companyId}/logo.webp`]);

    // Update company record
    await this.updateCompanyProfile(companyId, { logo_url: null });

    return { success: true };
  },

  // ==========================================
  // COMPANY SETTINGS (EXTENDED)
  // ==========================================

  /**
   * Get company settings (invoice, notifications, branding)
   * @param {string} companyId - Company UUID
   */
  async getCompanySettings(companyId) {
    // Try to ensure settings record exists (function may not exist if migration not applied)
    try {
      await supabase.rpc('ensure_company_settings', { p_company_id: companyId });
    } catch (e) {
      console.warn('ensure_company_settings RPC not available:', e.message);
    }

    const { data, error } = await supabase
      .from('company_settings')
      .select('*')
      .eq('company_id', companyId)
      .single();

    // If table doesn't exist or no record, return defaults
    if (error?.code === '42P01' || error?.code === 'PGRST116') {
      return { success: true, data: this.getDefaultCompanySettings() };
    }

    if (error) throw new Error(error.message);
    return { success: true, data };
  },

  /**
   * Default company settings
   */
  getDefaultCompanySettings() {
    return {
      invoice_prefix: 'INV',
      invoice_counter: 1,
      invoice_reset_frequency: 'yearly',
      payment_terms_text: 'Payment due within 30 days of invoice date.',
      invoice_footer_disclaimer: 'Thank you for your business.',
      default_tax_rate: 10.00,
      job_id_prefix: null,
      job_id_reset_frequency: 'monthly',
      primary_color: '#FF7A00',
      secondary_color: '#00C47D',
      hide_financials_in_reports: false,
      // Bank details for quotes
      bank_name: null,
      bsb: null,
      account_number: null,
      show_bank_details_on_quotes: true,
      // GST/ABN details
      abn: null,
      show_gst_on_quotes: true
    };
  },

  /**
   * Update company settings
   * @param {string} companyId - Company UUID
   * @param {object} updates - Fields to update
   */
  async updateCompanySettings(companyId, updates) {
    // First try upsert (insert or update)
    const { data, error } = await supabase
      .from('company_settings')
      .upsert({
        company_id: companyId,
        ...updates,
        updated_at: new Date().toISOString()
      }, { onConflict: 'company_id' })
      .select()
      .single();

    // If table doesn't exist, show a friendly message
    if (error?.code === '42P01') {
      throw new Error('Settings table not configured. Please run database migration.');
    }

    if (error) throw new Error(error.message);
    return { success: true, data };
  },

  // ==========================================
  // JOB TEMPLATES
  // ==========================================

  /**
   * Get all active job templates for a company
   * @param {string} companyId - Company UUID
   */
  async getJobTemplates(companyId) {
    const { data, error } = await supabase
      .from('job_templates')
      .select(`
        *,
        created_by_user:user_profiles!job_templates_created_by_fkey(
          full_name, email
        )
      `)
      .eq('company_id', companyId)
      .eq('is_active', true)
      .order('template_name');

    // If table doesn't exist, return empty array
    if (error?.code === '42P01') {
      return { success: true, data: [] };
    }

    if (error) throw new Error(error.message);
    return { success: true, data: data || [] };
  },

  /**
   * Create a job template
   * @param {string} companyId - Company UUID
   * @param {object} template - Template data
   */
  async createJobTemplate(companyId, template) {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('job_templates')
      .insert({
        company_id: companyId,
        template_name: template.name,
        category: template.category || null,
        description: template.description || null,
        template_data: template.data || {},
        is_shared: template.isShared || false,
        created_by: user.id
      })
      .select()
      .single();

    if (error) throw new Error(error.message);
    return { success: true, data };
  },

  /**
   * Update a job template
   * @param {string} templateId - Template UUID
   * @param {object} updates - Fields to update
   */
  async updateJobTemplate(templateId, updates) {
    const { data, error } = await supabase
      .from('job_templates')
      .update({
        template_name: updates.name,
        category: updates.category,
        description: updates.description,
        template_data: updates.data,
        is_shared: updates.isShared,
        updated_at: new Date().toISOString()
      })
      .eq('id', templateId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return { success: true, data };
  },

  /**
   * Delete (soft) a job template
   * @param {string} templateId - Template UUID
   */
  async deleteJobTemplate(templateId) {
    const { error } = await supabase
      .from('job_templates')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', templateId);

    if (error) throw new Error(error.message);
    return { success: true };
  },

  // ==========================================
  // NOTIFICATION PREFERENCES
  // ==========================================

  /**
   * Get user notification preferences for a company
   * @param {string} companyId - Company UUID
   */
  async getNotificationPreferences(companyId) {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', user.id)
      .eq('company_id', companyId)
      .single();

    // If no preferences exist, return defaults
    if (error?.code === 'PGRST116') {
      return {
        success: true,
        data: this.getDefaultNotificationPreferences()
      };
    }

    if (error) throw new Error(error.message);
    return { success: true, data };
  },

  /**
   * Update user notification preferences
   * @param {string} companyId - Company UUID
   * @param {object} preferences - Preferences to update
   */
  async updateNotificationPreferences(companyId, preferences) {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('notification_preferences')
      .upsert({
        user_id: user.id,
        company_id: companyId,
        ...preferences,
        updated_at: new Date().toISOString()
      }, { onConflict: 'user_id,company_id' })
      .select()
      .single();

    if (error) throw new Error(error.message);
    return { success: true, data };
  },

  /**
   * Default notification preferences
   */
  getDefaultNotificationPreferences() {
    return {
      email_enabled: true,
      sms_enabled: false,
      push_enabled: true,
      in_app_enabled: true,
      job_assignment_notify: true,
      job_update_notify: true,
      job_completion_notify: true,
      payment_notify: true,
      report_notify: true,
      daily_digest_enabled: false,
      weekly_digest_enabled: false,
      digest_time: '08:00',
      digest_day: 'monday'
    };
  },

  // ==========================================
  // USER PROFILE
  // ==========================================

  /**
   * Get current user's profile
   */
  async getUserProfile() {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('user_profiles')
      .select(`
        id,
        email,
        full_name,
        display_name,
        avatar_url,
        phone,
        job_title,
        bio,
        date_format,
        time_format,
        first_day_of_week,
        number_format,
        theme,
        language,
        trade,
        years_experience,
        certifications,
        abn,
        job_preferences,
        availability,
        created_at,
        updated_at
      `)
      .eq('id', user.id)
      .single();

    if (error) throw new Error(error.message);
    return { success: true, data };
  },

  /**
   * Update current user's profile
   * @param {object} updates - Fields to update
   */
  async updateUserProfile(updates) {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('user_profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return { success: true, data };
  },

  /**
   * Upload user avatar
   * @param {File} file - Image file to upload
   */
  async uploadAvatar(file) {
    const { data: { user } } = await supabase.auth.getUser();
    const fileExt = file.name.split('.').pop().toLowerCase();
    const fileName = `${user.id}/avatar.${fileExt}`;

    // Delete existing avatar files first
    await supabase.storage
      .from('user-avatars')
      .remove([`${user.id}/avatar.png`, `${user.id}/avatar.jpg`, `${user.id}/avatar.jpeg`, `${user.id}/avatar.webp`]);

    // Upload new avatar
    const { error: uploadError } = await supabase.storage
      .from('user-avatars')
      .upload(fileName, file, {
        upsert: true,
        cacheControl: '3600'
      });

    if (uploadError) throw new Error(uploadError.message);

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('user-avatars')
      .getPublicUrl(fileName);

    // Update user profile with new avatar URL (add cache buster)
    const avatarUrl = `${urlData.publicUrl}?t=${Date.now()}`;
    await this.updateUserProfile({ avatar_url: avatarUrl });

    return { success: true, url: avatarUrl };
  },

  /**
   * Delete user avatar
   */
  async deleteAvatar() {
    const { data: { user } } = await supabase.auth.getUser();

    // Remove all possible avatar files
    await supabase.storage
      .from('user-avatars')
      .remove([`${user.id}/avatar.png`, `${user.id}/avatar.jpg`, `${user.id}/avatar.jpeg`, `${user.id}/avatar.webp`]);

    // Update user profile
    await this.updateUserProfile({ avatar_url: null });

    return { success: true };
  },

  /**
   * Update display preferences
   * @param {object} prefs - Display preferences
   */
  async updateDisplayPreferences(prefs) {
    return this.updateUserProfile({
      date_format: prefs.date_format,
      time_format: prefs.time_format,
      first_day_of_week: prefs.first_day_of_week,
      number_format: prefs.number_format,
      theme: prefs.theme,
      language: prefs.language
    });
  },

  // ==========================================
  // SECURITY
  // ==========================================

  /**
   * Verify current password by re-authenticating
   * @param {string} currentPassword - Current password to verify
   * @returns {Promise<{success: boolean}>}
   */
  async verifyCurrentPassword(currentPassword) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.email) throw new Error('User not found');

    const { error } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword
    });

    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        throw new Error('Current password is incorrect');
      }
      throw new Error(error.message);
    }

    return { success: true };
  },

  /**
   * Change password with current password verification
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   */
  async changePasswordWithVerification(currentPassword, newPassword) {
    // First verify the current password
    await this.verifyCurrentPassword(currentPassword);

    // Then update to the new password
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) throw new Error(error.message);
    return { success: true };
  },

  /**
   * Update user password (direct - no verification)
   * @param {string} newPassword - New password
   */
  async updatePassword(newPassword) {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) throw new Error(error.message);
    return { success: true };
  },

  /**
   * Request OTP for password change
   * Sends OTP to email and optionally SMS if phone is available
   * @param {string} phoneNumber - Optional phone number for SMS
   */
  async requestPasswordChangeOTP(phoneNumber = null) {
    const deliveryMethod = phoneNumber ? 'both' : 'email';

    const { data, error } = await supabase.rpc('generate_otp', {
      p_purpose: 'password_reset',
      p_delivery_method: deliveryMethod,
      p_phone_number: phoneNumber
    });

    if (error) throw new Error(error.message);
    if (!data?.success) throw new Error(data?.error || 'Failed to generate OTP');

    // In production, you would integrate with email/SMS service here
    // For now, we'll use Supabase's built-in email or display the code in dev
    return {
      success: true,
      expiresAt: data.expires_at,
      emailMasked: data.email_masked,
      phoneMasked: data.phone_masked,
      deliveryMethod: data.delivery_method,
      // Only return code in development for testing
      code: import.meta.env.DEV ? data.code : undefined
    };
  },

  /**
   * Verify OTP and update password
   * @param {string} otpCode - The 6-digit OTP code
   * @param {string} newPassword - New password to set
   */
  async verifyOTPAndUpdatePassword(otpCode, newPassword) {
    // First verify the OTP
    const { data: verifyData, error: verifyError } = await supabase.rpc('verify_otp', {
      p_code: otpCode,
      p_purpose: 'password_reset'
    });

    if (verifyError) throw new Error(verifyError.message);
    if (!verifyData?.success) {
      throw new Error(verifyData?.error || 'Invalid OTP');
    }

    // OTP verified, now update password
    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (updateError) throw new Error(updateError.message);

    return { success: true };
  },

  /**
   * Sign out from all other sessions
   */
  async signOutOtherSessions() {
    const { error } = await supabase.auth.signOut({ scope: 'others' });
    if (error) throw new Error(error.message);
    return { success: true };
  },

  /**
   * Sign out from all sessions (including current)
   */
  async signOutAllSessions() {
    const { error } = await supabase.auth.signOut({ scope: 'global' });
    if (error) throw new Error(error.message);
    return { success: true };
  },

  // ==========================================
  // DATA EXPORT (GDPR)
  // ==========================================

  /**
   * Export user's personal data
   */
  async exportUserData() {
    const { data: { user } } = await supabase.auth.getUser();

    const exportData = {
      exported_at: new Date().toISOString(),
      user: {}
    };

    // Get user profile
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    exportData.user.profile = profile;

    // Get company memberships
    const { data: memberships } = await supabase
      .from('company_users')
      .select(`
        role,
        company_alias,
        is_active,
        companies(company_name, company_tag)
      `)
      .eq('user_id', user.id);
    exportData.user.memberships = memberships;

    // Get notification preferences
    const { data: notifPrefs } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', user.id);
    exportData.user.notification_preferences = notifPrefs;

    return {
      success: true,
      data: JSON.stringify(exportData, null, 2),
      format: 'json'
    };
  },

  /**
   * Export company data (requires export_data permission)
   * @param {string} companyId - Company UUID
   * @param {object} options - Export options
   */
  async exportCompanyData(companyId, options = {}) {
    const { format = 'json', includeJobs = true, includeUsers = true } = options;

    const exportData = {
      exported_at: new Date().toISOString(),
      company: {}
    };

    // Get company profile
    const { data: company } = await this.getCompanyProfile(companyId);
    exportData.company.profile = company;

    // Get settings
    try {
      const { data: settings } = await this.getCompanySettings(companyId);
      exportData.company.settings = settings;
    } catch (e) {
      exportData.company.settings = null;
    }

    if (includeJobs) {
      const { data: jobs } = await supabase
        .from('jobs')
        .select('*')
        .eq('company_id', companyId);
      exportData.company.jobs = jobs;
    }

    if (includeUsers) {
      const { data: users } = await supabase
        .from('company_users')
        .select(`
          role,
          company_alias,
          is_active,
          user_profiles(full_name, email)
        `)
        .eq('company_id', companyId);
      exportData.company.users = users;
    }

    if (format === 'csv') {
      return {
        success: true,
        data: this._toCSV(exportData),
        format: 'csv'
      };
    }

    return {
      success: true,
      data: JSON.stringify(exportData, null, 2),
      format: 'json'
    };
  },

  /**
   * Convert data to CSV format
   * @private
   */
  _toCSV(data) {
    const lines = ['Section,Key,Value'];

    const flatten = (obj, prefix = '') => {
      Object.entries(obj).forEach(([key, value]) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          flatten(value, fullKey);
        } else {
          const escapedValue = String(value).replace(/"/g, '""');
          lines.push(`"${prefix}","${key}","${escapedValue}"`);
        }
      });
    };

    flatten(data);
    return lines.join('\n');
  },

  // ==========================================
  // TEAM STATS (for Team & Permissions tab)
  // ==========================================

  /**
   * Get team summary stats
   * @param {string} companyId - Company UUID
   */
  async getTeamStats(companyId) {
    const { data, error } = await supabase
      .from('company_users')
      .select('role, is_active')
      .eq('company_id', companyId)
      .eq('is_active', true);

    if (error) throw new Error(error.message);

    const stats = {
      total: data.length,
      employees: 0,
      contractors: 0,
      managers: 0
    };

    const managerRoles = ['primary_owner', 'owner', 'executive', 'general_manager', 'department_head', 'manager'];

    data.forEach(member => {
      if (member.role === 'contractor') {
        stats.contractors++;
      } else {
        stats.employees++;
        if (managerRoles.includes(member.role)) {
          stats.managers++;
        }
      }
    });

    return { success: true, data: stats };
  }
};

export default settingsService;
